// The sound catalog: every pre-rendered one-shot and loop (§13.3 SFX, §13.5 voices and crowd, the
// announcer) with its bus, voice priority, level, captions, loop points and pre-render tier. Music
// instruments and drums are catalogued in music/instruments.ts and rendered by the same queue.
import { CROWD_SOUNDS, type CrowdSound } from './crowd/crowdSounds';
import { RECIPES, type Recipe, type SfxId } from './sfx/recipes';
import { ANNOUNCER, BEAN_LINES, BEAN_SYLLABLES, SYLLABLES, type AnnouncerWord, type BeanLine, type Syllable } from './voice/voices';

export type SoundBus = 'sfx' | 'ui' | 'voice' | 'crowd';

export interface LoopSpec { start: number; length: number; fade: number }

export interface SoundMeta {
  bus: SoundBus;
  /** Voice-steal priority 0..10 (higher survives). */
  pri: number;
  /** Level after loudness normalization, dB. */
  db: number;
  /** Rendered variants (§13.1: 3 per SFX). */
  variants: number;
  /** In tune: no random playbackRate jitter (bonk tones, dings, arpeggios). */
  tonal?: boolean;
  loop?: LoopSpec;
  caption?: string;
  /** Pre-render order: 0 essential (≤ 300 ms after unlock), 2 voices and common, 3 the rest. */
  tier: 0 | 2 | 3;
}

export interface SoundDef extends SoundMeta { key: string; recipe: Recipe }

type SfxMeta = Partial<SoundMeta> & { pri: number; db: number };

const UI = (db: number, caption?: string): SfxMeta => ({ bus: 'ui', pri: 9, db, tier: 0, caption });

/** Per-SFX metadata; unspecified fields default to the SFX bus, 3 variants, tier 3. */
const SFX_META: Readonly<Record<SfxId, SfxMeta>> = {
  bonk: { pri: 8, db: 6, tier: 0, caption: 'BONK!' },
  bonkClick: { pri: 8, db: -4, tier: 0 },
  subBoom: { pri: 7, db: -4, tier: 2 },
  pow: { pri: 8, db: 3, tier: 2, caption: 'POW!' },
  clang: { pri: 8, db: -5, tier: 2, tonal: true, caption: 'CLANG!' },
  whoosh: { pri: 4, db: -10, tier: 0 },
  yoink: { pri: 6, db: -7, tier: 2, caption: 'Yoink!' },
  whiff: { pri: 3, db: -13, tier: 2 },
  yeet: { pri: 6, db: -7, tier: 2, caption: 'YEET!' },
  jump: { pri: 3, db: -12, tier: 2 },
  land: { pri: 3, db: -11, tier: 2 },
  step: { pri: 1, db: -26, tier: 2 },
  bigStep: { pri: 4, db: -10, tier: 2 },
  teeterWobble: { pri: 6, db: -19, tier: 2, loop: { start: 0.1, length: 1, fade: 0.1 }, caption: 'Wobbling at the edge' },
  slideWhistle: { pri: 6, db: -12, tier: 2, caption: 'Falling scream' },
  opera: { pri: 6, db: -12, tier: 2, caption: 'Falling scream' },
  yodel: { pri: 6, db: -12, tier: 2, caption: 'Falling scream' },
  dopplerNooo: { pri: 6, db: -12, tier: 2, caption: 'Falling scream' },
  splooshTea: { pri: 9, db: 6, tier: 0, caption: 'SPLOOSH!' },
  splooshMilk: { pri: 9, db: 6, tier: 0, caption: 'SPLOOSH!' },
  splooshFoam: { pri: 9, db: 6, tier: 0, caption: 'SPLOOSH!' },
  splooshGrid: { pri: 9, db: 5, tier: 0, caption: 'ZAP!' },
  splooshLilac: { pri: 9, db: 5, tier: 0, caption: 'Sploosh!' },
  cratePop: { pri: 6, db: -7, tier: 2, tonal: true, caption: 'Crate opens' },
  cratePopMinor: { pri: 6, db: -7, tier: 2, tonal: true, caption: 'Crate opens' },
  crateWhistle: { pri: 4, db: -14, tier: 2, caption: 'Crate falling' },
  crateThump: { pri: 4, db: -9, tier: 2 },
  bombTick: { pri: 7, db: -4, tier: 2, tonal: true, caption: 'Bomb ticking' },
  explosion: { pri: 10, db: 0, tier: 2, caption: 'KABOOM!' },
  peelSplat: { pri: 4, db: -11, tier: 2 },
  slip: { pri: 6, db: -8, tier: 2, caption: 'Whoops! (slip)' },
  balloonStretch: { pri: 5, db: -11, tier: 2, caption: 'Balloon inflating' },
  balloonPop: { pri: 6, db: -6, tier: 2, caption: 'Pop!' },
  plungerThwop: { pri: 6, db: -6, tier: 2, caption: 'THWOP!' },
  suction: { pri: 4, db: -14, tier: 2 },
  yank: { pri: 5, db: -9, tier: 2 },
  shakeGlug: { pri: 6, db: -6, tier: 2, caption: 'Glug glug BWOOP' },
  bwoopDown: { pri: 5, db: -9, tier: 2 },
  gloveWindup: { pri: 6, db: -9, tier: 2, caption: 'Spring winding up' },
  bumperDing: { pri: 6, db: -9, tier: 2, tonal: true, caption: 'DING!' },
  flipperClack: { pri: 7, db: -3, tier: 2, caption: 'Flipper snaps' },
  pinballRumble: { pri: 5, db: -14, tier: 2, loop: { start: 0.15, length: 2, fade: 0.2 }, caption: 'Giant pinball rolling' },
  pinballLaunch: { pri: 7, db: -7, tier: 2, caption: 'Pinball launches' },
  tileCrack: { pri: 4, db: -12, tier: 2, caption: 'Frosting cracks' },
  tileFall: { pri: 4, db: -11, tier: 2 },
  regrowSparkle: { pri: 3, db: -17, tier: 3, tonal: true },
  regrowSparkleMinor: { pri: 3, db: -17, tier: 3, tonal: true },
  flameWhoosh: { pri: 7, db: -5, tier: 2, caption: 'Flame ring!' },
  wedgeCrack: { pri: 5, db: -9, tier: 2, caption: 'Porcelain cracking' },
  ceramicClatter: { pri: 6, db: -8, tier: 2, caption: 'Rim chip falls' },
  sweeperHum: { pri: 5, db: -16, tier: 2, loop: { start: 0.15, length: 2, fade: 0.2 } },
  reverseBuzzer: { pri: 8, db: -7, tier: 2, caption: 'Buzzer: arms reversing' },
  sweeperClunk: { pri: 6, db: -8, tier: 2 },
  bubblePop: { pri: 6, db: -8, tier: 2, caption: 'Bubble pops' },
  bubbleWobble: { pri: 4, db: -15, tier: 2 },
  cupWhirl: { pri: 5, db: -14, tier: 2, loop: { start: 0.15, length: 2, fade: 0.2 }, caption: 'Teacup whirling' },
  fling: { pri: 6, db: -7, tier: 2, caption: 'Flung!' },
  stompCrunch: { pri: 7, db: -5, tier: 2, caption: 'CRUNCH!' },
  escapePop: { pri: 6, db: -8, tier: 2, caption: 'Break free!' },
  porcelainCreak: { pri: 3, db: -15, tier: 3, caption: 'Saucer creaks' },
  clownHorn: { pri: 5, db: -9, tier: 2, caption: 'Honk!' },
  duck: { pri: 5, db: -9, tier: 2, caption: 'Quack!' },
  kazoo: { pri: 5, db: -10, tier: 2, caption: 'Kazoo!' },
  foghorn: { pri: 5, db: -8, tier: 2, caption: 'Foghorn!' },
  bikeBell: { pri: 5, db: -11, tier: 2, caption: 'Ring ring!' },
  squeaky: { pri: 5, db: -11, tier: 2, caption: 'Squeak!' },
  goose: { pri: 5, db: -9, tier: 2, caption: 'HONK!' },
  airhorn: { pri: 5, db: -9, tier: 2, caption: 'Airhorn!' },
  pebbleDrop: { pri: 3, db: -17, tier: 2 },
  pebbleTok: { pri: 4, db: -11, tier: 2, caption: 'Pebble tok' },
  parachute: { pri: 4, db: -11, tier: 3, caption: 'Parachute' },
  crownPop: { pri: 7, db: -7, tier: 2, tonal: true, caption: 'Crown pops off!' },
  crownPopMinor: { pri: 7, db: -7, tier: 2, tonal: true, caption: 'Crown pops off!' },
  crownPick: { pri: 7, db: -8, tier: 2, tonal: true, caption: 'Crown grabbed' },
  crownPickMinor: { pri: 7, db: -8, tier: 2, tonal: true, caption: 'Crown grabbed' },
  crownTick: { pri: 4, db: -14, tier: 2, tonal: true },
  crownLost: { pri: 6, db: -10, tier: 3, tonal: true },
  crownLostMinor: { pri: 6, db: -10, tier: 3, tonal: true },
  potatoBeep: { pri: 7, db: -9, tier: 2, tonal: true, caption: 'Hot potato ticking' },
  potatoBoom: { pri: 10, db: 0, tier: 2, caption: 'POTATO KABOOM!' },
  potatoPass: { pri: 6, db: -8, tier: 2, caption: 'Potato passed' },
  countdownBeep: { pri: 9, db: -9, tier: 0, tonal: true },
  ghostWhoo: { pri: 4, db: -12, tier: 3, caption: 'Ghostly whoo' },
  itemGet: { pri: 5, db: -12, tier: 2, tonal: true, caption: 'Item!' },
  vendClunk: { pri: 4, db: -10, tier: 3, caption: 'Vending machine' },
  headBoing: { pri: 6, db: -8, tier: 3, caption: 'Boing!' },
  tapeStop: { pri: 9, db: -8, tier: 2 },
  vhsRewind: { pri: 6, db: -14, tier: 3 },
  siren: { pri: 8, db: -10, tier: 2, caption: 'Siren!' },
  fuseLight: { pri: 7, db: -10, tier: 2, caption: 'Fuse lit' },
  candleFlicker: { pri: 4, db: -14, tier: 2, caption: 'Candle flickering' },
  flipperBlink: { pri: 5, db: -12, tier: 2, tonal: true },
  ballAlarm: { pri: 7, db: -11, tier: 2, tonal: true, caption: 'BALL LAUNCH!' },
  armDrop: { pri: 7, db: -7, tier: 3, caption: 'Third arm drops' },
  hover: UI(-18),
  click: UI(-12),
  back: UI(-13),
  error: UI(-13, 'Error buzz'),
  coin: { ...UI(-13), tonal: true },
  drumroll: UI(-10, 'Drumroll…'),
  cardFlip: UI(-11),
  toast: UI(-14),
  uiTick: UI(-17),
};

function sfxDef(id: SfxId): SoundDef {
  const m = SFX_META[id];
  return {
    key: id, recipe: RECIPES[id], bus: m.bus ?? 'sfx', pri: m.pri, db: m.db, variants: m.variants ?? 3,
    tonal: m.tonal, loop: m.loop, caption: m.caption, tier: m.tier ?? 3,
  };
}

export type SylKey = `syl.${Syllable}`;
export type LineKey = `line.${BeanLine}`;
export type AnnKey = `ann.${AnnouncerWord}`;
export type CrowdKey = `crowd.${CrowdSound}`;
export type SoundKey = SfxId | SylKey | LineKey | AnnKey | CrowdKey;

const VOICE_LINE_CAPTIONS: Readonly<Partial<Record<BeanLine, string>>> = {
  hit: '“Hwah!”', throw: '“Hup!”', teeter: '“Oh-oh-oh!”', ko: '“Ya-hoo!”', grabbed: '“Nyeh!”', stomp: '“Ow!”', hot: '“Hot!”',
};
const ANNOUNCER_CAPTIONS: Readonly<Record<AnnouncerWord, string>> = {
  three: 'Announcer: 3', two: 'Announcer: 2', one: 'Announcer: 1', bonk: 'Announcer: BONK!', suddenDeath: 'Announcer: SUDDEN DEATH!',
  matchPoint: 'Announcer: MATCH POINT!', game: 'Announcer: GAME!', doubleBonk: 'Announcer: DOUBLE BONK!', draw: 'Announcer: DRAW!',
};
const CROWD_META: Readonly<Record<CrowdSound, { db: number; caption?: string; loop?: LoopSpec }>> = {
  murmur: { db: -14, loop: { start: 0.45, length: 4, fade: 0.3 } },
  ooh: { db: -8, caption: 'Crowd: OOOH' },
  aah: { db: -7, caption: 'Crowd: AAAH!' },
  cheer: { db: -7, caption: 'Crowd cheers' },
  laugh: { db: -8, caption: 'Crowd: ho ho ho' },
  gasp: { db: -8, caption: 'Crowd gasps' },
  roar: { db: -5, caption: 'Crowd roars' },
  clap: { db: -13 },
};

function buildCatalog(): Map<SoundKey, SoundDef> {
  const out = new Map<SoundKey, SoundDef>();
  for (const id of Object.keys(RECIPES) as SfxId[]) out.set(id, sfxDef(id));
  for (const s of SYLLABLES) out.set(`syl.${s}`, { key: `syl.${s}`, recipe: BEAN_SYLLABLES[s], bus: 'voice', pri: 5, db: -10, variants: 1, tier: 2 });
  for (const l of Object.keys(BEAN_LINES) as BeanLine[]) {
    out.set(`line.${l}`, { key: `line.${l}`, recipe: BEAN_LINES[l], bus: 'voice', pri: 6, db: l === 'grunt' ? -16 : -9, variants: 2, tier: 2, caption: VOICE_LINE_CAPTIONS[l] });
  }
  for (const w of Object.keys(ANNOUNCER) as AnnouncerWord[]) {
    out.set(`ann.${w}`, { key: `ann.${w}`, recipe: ANNOUNCER[w], bus: 'ui', pri: 10, db: -11, variants: 1, tier: 2, caption: ANNOUNCER_CAPTIONS[w] });
  }
  for (const c of Object.keys(CROWD_SOUNDS) as CrowdSound[]) {
    const m = CROWD_META[c];
    out.set(`crowd.${c}`, { key: `crowd.${c}`, recipe: CROWD_SOUNDS[c], bus: 'crowd', pri: 5, db: m.db, variants: c === 'murmur' ? 1 : 2, tier: 2, caption: m.caption, loop: m.loop });
  }
  return out;
}

export const SOUNDS: ReadonlyMap<SoundKey, SoundDef> = buildCatalog();
export const SOUND_KEYS: readonly SoundKey[] = [...SOUNDS.keys()];

export function soundDef(key: SoundKey): SoundDef {
  const d = SOUNDS.get(key);
  if (!d) throw new Error(`unknown sound '${key}'`);
  return d;
}

/** The KO splash for a map's void (§11.2: liquid-specific). */
export const SPLOOSH_BY_MAP: readonly SfxId[] = ['splooshLilac', 'splooshLilac', 'splooshTea', 'splooshGrid', 'splooshMilk', 'splooshFoam'];

/** Cosmetic honks and fall screams by catalog id (§7.3 order). */
export const HONKS: readonly SfxId[] = ['clownHorn', 'duck', 'kazoo', 'foghorn', 'bikeBell', 'squeaky', 'goose', 'airhorn'];
export const SCREAMS: readonly SfxId[] = ['slideWhistle', 'opera', 'yodel', 'dopplerNooo'];
